import { useEffect, useState, useContext } from "react";
import api from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import {
  Box, Typography, Paper, Button, Divider, Avatar, TextField,
  IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PersonIcon from "@mui/icons-material/Person";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HistoryIcon from "@mui/icons-material/History";
import ReplayIcon from "@mui/icons-material/Replay";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SettingsIcon from "@mui/icons-material/Settings";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LogoutIcon from "@mui/icons-material/Logout";
import BuildIcon from "@mui/icons-material/Build";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PaymentsIcon from "@mui/icons-material/Payments";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import LoadingScreen from "../components/LoadingScreen";
import ProductCard from "../components/ProductCard";
import OrderDetailsDashboard from "./OrderDetailsDashboard";
import MobileReelCard from "../components/MobileReelCard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, Keyboard } from "swiper/modules";
import "swiper/css";

const colors = {
  deepNavy: "#131921", primary: "#0056c3", primaryContainer: "#1f6feb",
  secondaryContainer: "#fe9800", surfaceBright: "#f7f9fc", surfaceContainerLow: "#f2f4f7",
  surfaceContainer: "#eceef1", surfaceContainerHigh: "#e6e8eb", surfaceContainerLowest: "#ffffff",
  onSurface: "#191c1e", onSurfaceVariant: "#424754", textMuted: "#878787", textMain: "#212121",
  outlineVariant: "#c2c6d6", outline: "#727786", successGreen: "#388E3C", error: "#ba1a1a",
  warningAmber: "#F3A847", onPrimary: "#ffffff",
};

const SummaryCard = ({ icon, value, label, subtitle, onClick }) => (
  <Paper onClick={onClick} sx={{ p: 2, borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, cursor: onClick ? "pointer" : "default", boxShadow: "0 2px 4px rgba(0,0,0,0.08)", "&:hover": onClick ? { transform: "translateY(-2px)", boxShadow: "0 4px 8px rgba(0,0,0,0.12)", transition: "all 0.2s ease-in-out" } : {} }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      {icon}
      <Typography sx={{ fontWeight: 700, fontSize: 22, color: colors.textMain }}>{value}</Typography>
    </Box>
    <Typography sx={{ fontWeight: 600, fontSize: 11, color: colors.onSurfaceVariant, textTransform: "uppercase", letterSpacing: "0.5px", mt: 1 }}>{label}</Typography>
    <Typography sx={{ fontSize: 12, color: colors.textMuted, mt: 0.25 }}>{subtitle}</Typography>
  </Paper>
);

const UtilityCard = ({ icon, title, onClick, children }) => (
  <Paper onClick={onClick} sx={{ p: 2.5, borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, boxShadow: "0 2px 4px rgba(0,0,0,0.08)", cursor: onClick ? "pointer" : "default" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      {icon}
      <Typography sx={{ fontWeight: 600, fontSize: 16, color: colors.onSurface }}>{title}</Typography>
    </Box>
    {children}
  </Paper>
);

const renderSidebarContent = (sidebarItems, userName, user, navigate) => (
  <Box sx={{ width: 256, py: 2 }}>
    <Box sx={{ px: 3, pb: 2, mb: 1, display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
      <Avatar src={user?.profilePic || ""} sx={{ bgcolor: colors.primary, width: 44, height: 44 }}>{!user?.profilePic && <PersonIcon />}</Avatar>
      <Box><Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.textMain }}>Hello, {userName}</Typography></Box>
    </Box>
    <List sx={{ px: 1 }}>
      {sidebarItems.map((item) => (
        <ListItem key={item.label} onClick={item.onClick} sx={{ borderRadius: 1, mb: 0.5, cursor: "pointer", bgcolor: item.active ? "#d9e2ff" : "transparent", borderRight: item.active ? "4px solid" : "4px solid transparent", borderColor: item.active ? colors.primary : "transparent", "&:hover": { bgcolor: colors.surfaceContainerHigh } }}>
          <ListItemIcon sx={{ minWidth: 36, color: item.active ? colors.primary : colors.onSurfaceVariant }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} sx={{ "& .MuiListItemText-primary": { fontSize: 14, fontWeight: 600, color: item.active ? "#001944" : colors.onSurfaceVariant } }} />
        </ListItem>
      ))}
    </List>
    <Box sx={{ mt: "auto", pt: 3, px: 1, borderTop: "1px solid", borderColor: colors.outlineVariant, mx: 3 }}>
      <ListItem onClick={() => { localStorage.removeItem("token"); localStorage.removeItem("user"); navigate("/login"); }} sx={{ borderRadius: 1, cursor: "pointer", "&:hover": { bgcolor: colors.surfaceContainerHigh } }}>
        <ListItemIcon sx={{ minWidth: 36, color: colors.onSurfaceVariant }}><LogoutIcon /></ListItemIcon>
        <ListItemText primary="Logout" sx={{ "& .MuiListItemText-primary": { fontSize: 14, fontWeight: 600, color: colors.onSurfaceVariant } }} />
      </ListItem>
    </Box>
  </Box>
);

const OrdersSection = ({ user, navigate, orderFilter, toggleWishlist, isWishlisted }) => {
  const { addToCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [buyAgainProducts, setBuyAgainProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [buyAgainActiveIndex, setBuyAgainActiveIndex] = useState(0);
  const [buyAgainAudio, setBuyAgainAudio] = useState(false);

  const fetchOrders = async (pageNum, append) => {
    if (!user?.username) return;
    try {
      const filterParam = orderFilter === "in-transit" ? "&filter=in-transit" : orderFilter === "manufacturing" ? "&filter=manufacturing" : "";
      const res = await api.get(`/orders/customer/${user.username}?page=${pageNum}&limit=20${filterParam}`);
      if (append) {
        setOrders((prev) => [...prev, ...(res.data.orders || [])]);
      } else {
        setOrders(res.data.orders || []);
      }
      setHasMore(res.data.hasMore || false);
    } catch {
      if (!append) setOrders([]);
    }
  };

  useEffect(() => {
    if (!user?.username) return;
    if (orderFilter === "buy-again") {
      api.get(`/orders/customer/${user.username}?type=buy-again`)
        .then((res) => setBuyAgainProducts(res.data.buyAgainProducts || []))
        .catch(() => setBuyAgainProducts([]))
        .finally(() => setLoading(false));
    } else {
      setPage(1);
      setLoading(true);
      fetchOrders(1, false).finally(() => setLoading(false));
    }
  }, [user?.username, orderFilter]);

  const handleLoadMore = async () => {
    setPage(page + 1);
    setLoadingMore(true);
    await fetchOrders(page + 1, true);
    setLoadingMore(false);
  };

  if (loading) return <LoadingScreen message="Loading orders..." />;

  const headerLabel = orderFilter === "in-transit" ? "In Transit Orders" : orderFilter === "manufacturing" ? "Under Manufacturing" : orderFilter === "buy-again" ? "Buy Again" : "My Orders";

  if (orderFilter === "buy-again") {
    if (buyAgainProducts.length === 0) {
      return <Paper sx={{ p: 4, textAlign: "center", borderRadius: 1 }}><Typography sx={{ color: colors.textMuted }}>No buy again items yet</Typography></Paper>;
    }
    return (
      <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>{headerLabel} ({buyAgainProducts.length})</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {/* MOBILE: full-screen vertical swiper */}
          <Box sx={{ display: { xs: "block", sm: "none" }, position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", zIndex: 1200, bgcolor: "#000" }}>
            <Swiper direction="vertical" modules={[Mousewheel, Keyboard]} mousewheel={{ forceToAxis: true }} keyboard={{ enabled: true }}
              onSlideChange={(swiper) => setBuyAgainActiveIndex(swiper.activeIndex)}
              style={{ width: "100%", height: "100%", overflow: "hidden" }}>
              {buyAgainProducts.map((item, index) => (
                <SwiperSlide key={item.productId} style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
                  <MobileReelCard item={item} isActive={index === buyAgainActiveIndex}
                    onNavigate={navigate}
                    audioEnabled={buyAgainAudio}
                    onToggleAudio={() => setBuyAgainAudio(!buyAgainAudio)}
                    onToggle={(p) => toggleWishlist({ id: p.productId, title: p.title, price: p.price, imageUrl: p.imageUrl })}
                    onBuyProduct={(id) => navigate(`/delivery-location/${id}`)} />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>

          {/* DESKTOP: grid layout */}
          <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 2 }}>
            {buyAgainProducts.map((item) => (
              <ProductCard key={item.productId} product={item} variant="buy-again"
                onNavigate={(id) => navigate(`/product/${id}`)}
                onAddToCart={(p) => addToCart({ id: item.productId, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                onBuyProduct={(id) => navigate(`/delivery-location/${id}`)}
                onToggleFavorite={(p) => toggleWishlist({ id: p.productId, title: p.title, price: p.price, imageUrl: p.imageUrl })}
                isWishlisted={isWishlisted(item.productId)} />
            ))}
          </Box>
        </Box>
      </Paper>
    );
  }

  if (!orders.length) {
    return <Paper sx={{ p: 4, textAlign: "center", borderRadius: 1 }}><Typography sx={{ color: colors.textMuted }}>No orders found</Typography></Paper>;
  }

  return (
    <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
        <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>{headerLabel}</Typography>
      </Box>
      <Box>
        {orders.map((order, index) => {
          const firstItem = order.items?.[0];
          const extraCount = (order.items?.length || 1) - 1;
          const lastTracking = order.tracking?.at(-1);
          const isDelivered = lastTracking?.label === "DELIVERED";
          return (
            <Box key={order._id}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "flex-start", md: "center" }, gap: 2, p: { xs: 2, md: 2.5 }, "&:hover": { bgcolor: colors.surfaceContainerLow } }}>
                <Box sx={{ width: 80, height: 80, flexShrink: 0, border: "1px solid", borderColor: colors.outlineVariant, borderRadius: 1, p: 0.5, bgcolor: colors.onPrimary, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {firstItem?.imageUrl ? <Box component="img" src={firstItem.imageUrl} alt={firstItem.title} sx={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <Typography variant="caption" sx={{ color: colors.textMuted }}>No Image</Typography>}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.textMain, wordBreak: "break-word", overflowWrap: "break-word", maxWidth: { xs: "30ch", md: "60ch" } }}>
                                {firstItem?.title || "Order Items"}
                                {extraCount > 0 && <Typography component="span" sx={{ fontSize: 12, color: colors.primary, ml: 0.5 }}>+{extraCount} more item{extraCount > 1 ? "s" : ""}</Typography>}
                              </Typography>
                  <Typography sx={{ fontSize: 12, color: colors.textMuted, mt: 0.5 }}>Order #{order._id.slice(-8)}</Typography>
                  <Typography sx={{ fontSize: 12, color: colors.textMuted }}>Purchased on {new Date(order.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, alignItems: { xs: "center", md: "flex-end" }, gap: 1, width: { xs: "100%", md: "auto" }, justifyContent: { xs: "space-between", md: "flex-start" } }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 22, color: colors.textMain }}>₹{Number(order.total).toLocaleString("en-IN")}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10, bgcolor: isDelivered ? "rgba(56,142,60,0.1)" : "rgba(254,152,0,0.1)", border: "1px solid", borderColor: isDelivered ? "rgba(56,142,60,0.2)" : "rgba(254,152,0,0.2)" }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: isDelivered ? colors.successGreen : colors.secondaryContainer, animation: isDelivered ? "none" : "pulse 2s infinite" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDelivered ? colors.successGreen : colors.secondaryContainer }}>{lastTracking?.label || "Processing"}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, gap: 1 }}>
                  <Button variant="contained" size="small" onClick={() => navigate(`/customer/dashboard/orders/${order._id}`)} sx={{ bgcolor: "#FFD814", color: "#000", fontSize: 11, fontWeight: 600, textTransform: "none", whiteSpace: "nowrap", minHeight: 28, "&:hover": { bgcolor: "#e6c512" } }}>{isDelivered ? "View Order" : "Track Order"}</Button>
                  <Button variant="outlined" size="small" sx={{ borderColor: colors.outlineVariant, color: colors.onSurfaceVariant, fontSize: 12, fontWeight: 600, textTransform: "none", whiteSpace: "nowrap", "&:hover": { bgcolor: colors.surfaceContainer } }}>Invoice</Button>
                </Box>
              </Box>
              {index < orders.length - 1 && <Divider sx={{ borderColor: colors.outlineVariant, mx: { xs: 2, md: 3 } }} />}
            </Box>
          );
        })}
      </Box>
      {hasMore && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore} sx={{ borderColor: colors.outlineVariant, color: colors.primary, textTransform: "none", fontWeight: 600, px: 4 }}>{loadingMore ? "Loading..." : "Load More"}</Button>
        </Box>
      )}
    </Paper>
  );
};

const WishlistSection = ({ navigate }) => {
  const { clearWishlist, toggleWishlist, isWishlisted } = useWishlist();
  const { addToCart } = useCart();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [wishlistAudio, setWishlistAudio] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get("/wishlist").then((res) => {
      if (!mounted) return;
      const mapped = (res.data?.items || []).map((item) => ({
        ...item,
        id: item.id || item._id || item.productId,
        productId: item.productId || item.id || item._id,
      }));
      setItems(mapped);
    }).catch(() => { if (mounted) setItems([]); }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) return <LoadingScreen message="Loading wishlist..." />;

  if (!items.length) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 1 }}>
        <Box sx={{ fontSize: 60, mb: 2, opacity: 0.3 }}>🎬</Box>
        <Typography sx={{ color: colors.textMuted, mb: 1 }}>Your wishlist is empty</Typography>
        <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: colors.primary, color: colors.onPrimary, textTransform: "none", borderRadius: "100px", px: 4 }}>Browse Reels</Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
        <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>Saved Items ({items.length})</Typography>
        <Button size="small" onClick={clearWishlist} sx={{ color: colors.textMuted, textTransform: "none", fontSize: 13, "&:hover": { color: colors.error } }}>Clear all</Button>
      </Box>
      <Box sx={{ p: 2 }}>
        {/* MOBILE: full-screen vertical swiper */}
        <Box sx={{ display: { xs: "block", sm: "none" }, position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", zIndex: 1200, bgcolor: "#000" }}>
          <Swiper direction="vertical" modules={[Mousewheel, Keyboard]} mousewheel={{ forceToAxis: true }} keyboard={{ enabled: true }}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            style={{ width: "100%", height: "100%", overflow: "hidden" }}>
            {items.map((item, index) => {
              const itemId = item.id || item._id || item.productId;
              return (
                <SwiperSlide key={itemId} style={{ height: "100%", margin: 0, padding: 0, overflow: "hidden" }}>
                  <MobileReelCard item={item} isActive={index === activeIndex}
                    onNavigate={navigate}
                    audioEnabled={wishlistAudio}
                    onToggleAudio={() => setWishlistAudio(!wishlistAudio)}
                    onAddToCart={(p) => addToCart({ id: itemId, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                    onToggle={toggleWishlist}
                    onBuyProduct={(id) => navigate(`/delivery-location/${id}`)} />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Box>

        {/* DESKTOP: grid layout */}
        <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 2 }}>
          {items.map((item) => {
            const itemId = item.id || item._id || item.productId;
            return (
              <ProductCard key={itemId} product={item} variant="wishlist"
                onNavigate={(id) => navigate(`/product/${id}`)}
                onBuyProduct={(id) => navigate(`/delivery-location/${id}`)}
                onAddToCart={(p) => addToCart({ id: itemId, title: p.title, price: Number(String(p.price).replace(/,/g, "")) || 0, imageUrl: p.imageUrl || p.images?.[0]?.thumbnailUrl || "", qty: 1 })}
                onToggleFavorite={toggleWishlist}
                isWishlisted={isWishlisted(itemId)} />
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

const CustomerDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { wishlist, toggleWishlist, isWishlisted } = useWishlist();

  const pathAfterDashboard = location.pathname.replace("/customer/dashboard", "").replace(/^\//, "");
  const pathParts = pathAfterDashboard.split("/");
  const activeSection = pathParts[0] || "dashboard";
  const orderId = pathParts[0] === "orders" && pathParts[1] ? pathParts[1] : null;

  const [totalOrders, setTotalOrders] = useState(0);
  const [inTransitOrders, setInTransitOrders] = useState(0);
  const [manufacturingOrders, setManufacturingOrders] = useState(0);
  const [recentOrders, setRecentOrders] = useState([]);
  const [buyAgainTotal, setBuyAgainTotal] = useState(0);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [orderFilter, setOrderFilter] = useState(null);

  useEffect(() => {
    if (!user?.username) return;
    api.get(`/orders/customer/${user.username}/dashboard`).then((res) => {
      setTotalOrders(res.data.totalOrders);
      setInTransitOrders(res.data.inTransitOrders || 0);
      setManufacturingOrders(res.data.manufacturingOrders || 0);
      setRecentOrders(res.data.recentOrders);
      setBuyAgainTotal(res.data.buyAgainTotal || 0);
    }).catch(() => {
      setTotalOrders(0); setInTransitOrders(0); setManufacturingOrders(0);
      setRecentOrders([]); setBuyAgainTotal(0);
    });
  }, [user?.username]);

  useEffect(() => {
    if (user) {
      // Fetch from backend for logged-in users
      api.get("/browse-history").then((res) => {
        setRecentlyViewed((res.data.products || []).slice(0, 3));
      }).catch(() => {});
    } else {
      // Fallback to localStorage for guests
      const ids = JSON.parse(localStorage.getItem("recentlyViewed")) || [];
      if (!ids.length) return;
      api.post("/products/recently-viewed", { ids: ids.slice(0, 3) }).then((res) => setRecentlyViewed(res.data.products || [])).catch(() => {});
    }
  }, [user]);

  const userName = user?.firstName || user?.username || "Customer";

  const sidebarItems = [
    { icon: <DashboardIcon sx={{ color: colors.primary }} />, label: "Dashboard", active: activeSection === "dashboard", onClick: () => navigate("/customer/dashboard") },
    { icon: <Inventory2Icon sx={{ color: colors.primary }} />, label: "My Orders", active: activeSection === "orders" && !orderFilter, onClick: () => { setOrderFilter(null); navigate("/customer/dashboard/orders"); } },
    { icon: <BuildIcon sx={{ color: colors.warningAmber }} />, label: "Under Manufacturing", active: activeSection === "orders" && orderFilter === "manufacturing", onClick: () => { setOrderFilter("manufacturing"); navigate("/customer/dashboard/orders"); } },
    { icon: <LocalShippingIcon sx={{ color: colors.secondaryContainer }} />, label: "In Transit", active: activeSection === "orders" && orderFilter === "in-transit", onClick: () => { setOrderFilter("in-transit"); navigate("/customer/dashboard/orders"); } },
    { icon: <FavoriteIcon sx={{ color: colors.error }} />, label: "Wishlist", active: activeSection === "wishlist", onClick: () => navigate("/customer/dashboard/wishlist") },
    { icon: <ReplayIcon sx={{ color: colors.successGreen }} />, label: "Buy Again", active: activeSection === "orders" && orderFilter === "buy-again", onClick: () => { setOrderFilter("buy-again"); navigate("/customer/dashboard/orders"); } },
    { icon: <SettingsIcon sx={{ color: colors.onSurfaceVariant }} />, label: "Account Settings", active: activeSection === "settings", onClick: () => navigate("/register/customer") },
    { icon: <SupportAgentIcon sx={{ color: colors.secondaryContainer }} />, label: "Support", active: activeSection === "support", onClick: () => navigate("/customer/dashboard/support") },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: colors.surfaceBright }}>
      <Drawer anchor="left" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}><IconButton onClick={() => setMobileMenuOpen(false)}><CloseIcon /></IconButton></Box>
        {renderSidebarContent(sidebarItems, userName, user, navigate)}
      </Drawer>
      <Box sx={{ display: "flex", gap: 2, px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 } }}>
        <Box sx={{ display: { xs: "none", md: "block" }, flexShrink: 0 }}>
          <Paper sx={{ width: 256, position: "sticky", top: 80, borderRadius: 1, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
            {renderSidebarContent(sidebarItems, userName, user, navigate)}
          </Paper>
        </Box>
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {activeSection === "dashboard" && (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 2 }}>
                <SummaryCard icon={<Inventory2Icon sx={{ fontSize: 32, color: colors.primary }} />} value={totalOrders} label="My Orders" subtitle="Past 6 months" onClick={() => { setOrderFilter(null); navigate("/customer/dashboard/orders"); }} />
                <SummaryCard icon={<BuildIcon sx={{ fontSize: 32, color: colors.warningAmber }} />} value={manufacturingOrders} label="Under Manufacturing" subtitle="Being produced" onClick={() => { setOrderFilter("manufacturing"); navigate("/customer/dashboard/orders"); }} />
                <SummaryCard icon={<LocalShippingIcon sx={{ fontSize: 32, color: colors.secondaryContainer }} />} value={inTransitOrders} label="In Transit" subtitle="Orders in progress" onClick={() => { setOrderFilter("in-transit"); navigate("/customer/dashboard/orders"); }} />
                <SummaryCard icon={<FavoriteIcon sx={{ fontSize: 32, color: colors.error }} />} value={wishlist.length} label="Wishlist Items" subtitle="Saved for later" onClick={() => navigate("/customer/dashboard/wishlist")} />
                <SummaryCard icon={<ReplayIcon sx={{ fontSize: 32, color: colors.successGreen }} />} value={buyAgainTotal} label="Buy Again" subtitle="Previously ordered" onClick={() => buyAgainTotal > 0 && (setOrderFilter("buy-again"), navigate("/customer/dashboard/orders"))} />
              </Box>
              <Paper sx={{ borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
                <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid", borderColor: colors.outlineVariant }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface }}>Recent Orders</Typography>
                  <Button onClick={() => navigate("/customer/dashboard/orders")} sx={{ color: colors.primary, fontWeight: 600, fontSize: 13, textTransform: "none" }}>View All Orders</Button>
                </Box>
                <Box sx={{ borderTop: "none" }}>
                  {!recentOrders.length ? (
                    <Box sx={{ p: 4, textAlign: "center" }}><Typography sx={{ color: colors.textMuted, fontSize: 14 }}>No orders yet</Typography></Box>
                  ) : (
                    recentOrders.map((entry, index) => {
                      // entry can be: { groupId, orders: [...] } for multi-vendor OR regular order object
                      const isGroup = entry.orders && Array.isArray(entry.orders);
                      const orders = isGroup ? entry.orders : [entry];
                      const firstItem = orders[0]?.items?.[0];
                      const combinedTotal = orders.reduce((sum, o) => sum + (o.total || 0), 0);
                      const combinedItems = orders.reduce((sum, o) => sum + (o.items?.length || 0), 0);
                      const displayId = isGroup ? entry.groupId : entry._id;
                      const createdAt = orders[0]?.createdAt;
                      return (
                        <Box key={displayId?.toString() || entry._id}>
                          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "flex-start", md: "center" }, gap: 2, p: { xs: 2, md: 2.5 }, "&:hover": { bgcolor: colors.surfaceContainerLow } }}>
                            <Box sx={{ width: 80, height: 80, flexShrink: 0, border: "1px solid", borderColor: colors.outlineVariant, borderRadius: 1, p: 0.5, bgcolor: colors.onPrimary, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {firstItem?.imageUrl ? <Box component="img" src={firstItem.imageUrl} alt={firstItem.title} sx={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <Typography variant="caption" sx={{ color: colors.textMuted }}>No Image</Typography>}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.textMain, wordBreak: "break-word", overflowWrap: "break-word", maxWidth: { xs: "30ch", md: "60ch" } }}>
                                {firstItem?.title || "Order Items"}
                                {combinedItems > 1 && <Typography component="span" sx={{ fontSize: 12, color: colors.primary, ml: 0.5 }}>+{combinedItems - 1} more item{combinedItems - 1 > 1 ? "s" : ""}</Typography>}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: colors.textMuted, mt: 0.5 }}>{isGroup ? "Group" : "Order"} #{displayId?.toString().slice(-8)}</Typography>
                              <Typography sx={{ fontSize: 12, color: colors.textMuted }}>Purchased on {new Date(createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</Typography>
                              {isGroup && (
                                <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                                  {orders.map((sub) => {
                                    const lastLabel = sub.tracking?.at(-1)?.label || "Processing";
                                    const isSubDelivered = lastLabel === "Order Delivered";
                                    return (
                                      <Box key={sub._id} sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: 10, bgcolor: isSubDelivered ? "rgba(56,142,60,0.1)" : "rgba(254,152,0,0.1)", border: "1px solid", borderColor: isSubDelivered ? "rgba(56,142,60,0.2)" : "rgba(254,152,0,0.2)" }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: isSubDelivered ? colors.successGreen : colors.secondaryContainer }} />
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: isSubDelivered ? colors.successGreen : colors.secondaryContainer, whiteSpace: "nowrap" }}>{lastLabel}</Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, alignItems: { xs: "center", md: "flex-end" }, gap: 1, width: { xs: "100%", md: "auto" }, justifyContent: { xs: "space-between", md: "flex-start" } }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 22, color: colors.textMain }}>₹{Number(combinedTotal).toLocaleString("en-IN")}</Typography>
                              {!isGroup && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10, bgcolor: "rgba(254,152,0,0.1)", border: "1px solid", borderColor: "rgba(254,152,0,0.2)" }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.secondaryContainer }}>{orders[0]?.tracking?.at(-1)?.label || "Processing"}</Typography>
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, gap: 1 }}>
                              <Button variant="contained" size="small" onClick={() => navigate(isGroup ? `/customer/dashboard/orders/${orders[0]._id}` : `/customer/dashboard/orders/${entry._id}`)} sx={{ bgcolor: "#FFD814", color: "#000", fontSize: 11, fontWeight: 600, textTransform: "none", whiteSpace: "nowrap", minHeight: 28, "&:hover": { bgcolor: "#e6c512" } }}>Track Order</Button>
                              <Button variant="outlined" size="small" sx={{ borderColor: colors.outlineVariant, color: colors.onSurfaceVariant, fontSize: 12, fontWeight: 600, textTransform: "none", whiteSpace: "nowrap", "&:hover": { bgcolor: colors.surfaceContainer } }}>Invoice</Button>
                            </Box>
                          </Box>
                          {index < recentOrders.length - 1 && <Divider sx={{ borderColor: colors.outlineVariant, mx: { xs: 2, md: 3 } }} />}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
                <UtilityCard icon={<LocationOnIcon sx={{ color: colors.primary }} />} title="Your Addresses" onClick={() => navigate("/register/customer")}>
                  <Box sx={{ p: 1.5, border: "2px solid #d9e2ff", borderRadius: 1, bgcolor: colors.surfaceContainerLow, mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.textMain }}>Home (Default)</Typography>
                    <Typography sx={{ fontSize: 12, color: colors.onSurfaceVariant, mt: 0.5, lineHeight: 1.5 }}>{user?.firstName} {user?.lastName}<br />{user?.addresses?.[0]?.addressLine1 || "No address set"}</Typography>
                  </Box>
                  <Button fullWidth variant="outlined" onClick={() => navigate("/register/customer")} sx={{ borderColor: colors.outline, color: colors.primary, fontWeight: 600, fontSize: 13, textTransform: "none" }}>Edit Addresses</Button>
                </UtilityCard>
                <UtilityCard icon={<HistoryIcon sx={{ color: colors.secondaryContainer }} />} title="Browsing History" onClick={() => navigate("/browse-history")}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2 }}>
                    {[0, 1, 2].map((i) => (
                      <Box key={i} sx={{ aspectRatio: "1/1", border: "1px solid", borderColor: colors.outlineVariant, borderRadius: 1, overflow: "hidden", bgcolor: colors.surfaceContainerLow, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {recentlyViewed[i]?.imageUrl ? <Box component="img" src={recentlyViewed[i].imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <HistoryIcon sx={{ fontSize: 24, color: colors.textMuted }} />}
                      </Box>
                    ))}
                  </Box>
                  <Button fullWidth variant="outlined" onClick={() => navigate("/browse-history")} sx={{ borderColor: colors.outline, color: colors.onSurfaceVariant, fontWeight: 600, fontSize: 13, textTransform: "none" }}>View Full History</Button>
                </UtilityCard>
                <UtilityCard icon={<SupportAgentIcon sx={{ color: "#9a4100" }} />} title="Customer Service">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                    {[{ icon: <ReplayIcon sx={{ fontSize: 18 }} />, label: "Returns & Refunds" }, { icon: <PaymentsIcon sx={{ fontSize: 18 }} />, label: "Payment Issues" }, { icon: <SupportAgentIcon sx={{ fontSize: 18 }} />, label: "Live Chat Support" }].map((item) => (
                      <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover": { color: colors.primary } }}>{item.icon}<Typography sx={{ fontSize: 13, color: "inherit" }}>{item.label}</Typography></Box>
                    ))}
                  </Box>
                  <Button fullWidth variant="contained" sx={{ bgcolor: colors.deepNavy, color: colors.onPrimary, fontWeight: 600, fontSize: 13, textTransform: "none", "&:hover": { opacity: 0.9 } }}>Contact Us</Button>
                </UtilityCard>
              </Box>
            </>
          )}
          {(activeSection === "orders" || activeSection === "manufacturing" || activeSection === "inTransit" || activeSection === "buyAgain") && !orderId && <OrdersSection user={user} navigate={navigate} orderFilter={orderFilter} toggleWishlist={toggleWishlist} isWishlisted={isWishlisted} />}
          {activeSection === "wishlist" && <WishlistSection navigate={navigate} />}
          {activeSection === "settings" && (
            <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
              <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface, mb: 3 }}>Account Settings</Typography>
              <TextField fullWidth label="First Name" defaultValue={user?.firstName || ""} size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label="Last Name" defaultValue={user?.lastName || ""} size="small" sx={{ mb: 2 }} />
              <TextField fullWidth label="Phone" defaultValue={user?.phone || ""} size="small" sx={{ mb: 2 }} />
              <Button variant="contained" sx={{ bgcolor: colors.primary, textTransform: "none" }}>Save Changes</Button>
            </Paper>
          )}
          {activeSection === "support" && (
            <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
              <Typography sx={{ fontWeight: 600, fontSize: 18, color: colors.onSurface, mb: 2 }}>Support</Typography>
              <Typography sx={{ color: colors.onSurfaceVariant, fontSize: 14, mb: 1 }}>Need help with an order or have a question? Our support team is here to help.</Typography>
              <Button variant="contained" fullWidth sx={{ bgcolor: colors.deepNavy, color: colors.onPrimary, textTransform: "none", mb: 1 }}>Contact Support</Button>
              <Button variant="outlined" fullWidth sx={{ borderColor: colors.outlineVariant, color: colors.onSurfaceVariant, textTransform: "none" }}>Frequently Asked Questions</Button>
            </Paper>
          )}
          {orderId && <OrderDetailsDashboard />}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerDashboard;